import React, { useState, useEffect, useMemo } from 'react';
import { 
  X, 
  Search,
  ChevronDown,
  ChevronUp,
  Download,
  User,
  Filter,
  TrendingUp,
  Clock,
  CheckCircle2,
  AlertCircle,
  FileSpreadsheet,
  Banknote,
  CalendarDays,
  Accessibility,
  ArrowDownRight,
  Users
} from 'lucide-react';

import { useLoader } from '../contexts/LoaderContext';
import { useParamsCompta } from '../contexts/ParametresComptaContext';

// Couleurs HoraPilot
const COLORS = {
  primary: '#448696',      // Bleu turquoise
  secondary: '#fed774',    // Jaune
  accent: '#ef4444',       // Rouge pour retenues
  green: '#22c55e'         // Vert pour validations
};

const HoraPilotPaiementHistory = ({ loadData, onDownloadFiche, showToast }) => {
  const [historique, setHistorique] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatut, setFilterStatut] = useState('tous');
  const [filterMois, setFilterMois] = useState('tous');
  const [filterAnnee, setFilterAnnee] = useState('tous');
  const [expandedRow, setExpandedRow] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const { showLoader, hideLoader } = useLoader();
  const { formatMontant } = useParamsCompta();


  const monthNames = [
    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
  ];

  const monthAbbrev = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];

  useEffect(() => {
    loadHistorique();
  }, []);

  const loadHistorique = async () => {
    showLoader();
    setLoading(true);
    try {
      const data = await loadData();
      console.log('📊 Historique chargé:', data.length, 'paiements');
      setHistorique(data);
    } catch (error) {
      console.error('Erreur chargement historique:', error);
      if (showToast) {
        showToast('Erreur lors du chargement de l\'historique', 'error');
      }
    } finally {
      setLoading(false);
      hideLoader();
    }
  };

  const handleDownloadFiche = async (paiementId) => {
    try {
      if (onDownloadFiche) {
        await onDownloadFiche(paiementId);
      } else {
        if (showToast) {
          showToast('Fonction de téléchargement non configurée', 'error');
        }
      }
    } catch (error) {
      console.error('Erreur téléchargement fiche:', error);
      if (showToast) {
        showToast('Erreur lors du téléchargement', 'error');
      }
    }
  };

  const anneesUniques = useMemo(() => 
    [...new Set(historique.map(p => p.annee))].sort((a, b) => b - a), 
    [historique]
  );
  
  const moisUniques = useMemo(() => 
    [...new Set(historique.map(p => p.mois))].sort((a, b) => a - b), 
    [historique]
  );

  const paiementsFiltres = useMemo(() => {
    return historique.filter(paiement => {
      const matchSearch = 
        `${paiement.employe_prenom} ${paiement.employe_nom}`
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        paiement.mode_paiement_nom?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchStatut = filterStatut === 'tous' || paiement.statut === filterStatut;
      const matchMois = filterMois === 'tous' || paiement.mois === parseInt(filterMois);
      const matchAnnee = filterAnnee === 'tous' || paiement.annee === parseInt(filterAnnee);
      
      return matchSearch && matchStatut && matchMois && matchAnnee;
    });
  }, [historique, searchTerm, filterStatut, filterMois, filterAnnee]);

  const stats = useMemo(() => ({
    total: paiementsFiltres.length,
    totalMontant: paiementsFiltres.reduce((sum, p) => sum + parseFloat(p.montant_net || 0), 0),
    totalBrut: paiementsFiltres.reduce((sum, p) => sum + parseFloat(p.montant_brut || 0), 0),
    totalRetenues: paiementsFiltres.reduce((sum, p) => sum + parseFloat(p.montant_retenues || 0) + parseFloat(p.montant_avances || 0), 0),
    clotures: paiementsFiltres.filter(p => p.statut === 'cloture').length,
    enCours: paiementsFiltres.filter(p => p.statut === 'paye').length
  }), [paiementsFiltres]);

  const getMoisNom = (mois) => monthNames[mois - 1] || mois;
  const getMoisAbbrev = (mois) => monthAbbrev[mois - 1] || mois;

  const hasActiveFilters = searchTerm || filterStatut !== 'tous' || filterMois !== 'tous' || filterAnnee !== 'tous';
  const activeFilterCount = [searchTerm, filterStatut !== 'tous', filterMois !== 'tous', filterAnnee !== 'tous'].filter(Boolean).length;

  const resetFilters = () => {
    setSearchTerm('');
    setFilterStatut('tous');
    setFilterMois('tous');
    setFilterAnnee('tous');
  };

 

  return (
      <div className="w-full h-full bg-white overflow-hidden flex flex-col">
        {/* Header */}
        <header className="flex-shrink-0">
        

          {/* KPI Strip */}
          <div className="px-8 py-6 bg-slate-50/50 border-b border-slate-200">
            <div className="grid grid-cols-4 gap-4">
              {/* Total décaissé */}
              <div className="p-4 bg-white border border-slate-200 rounded-xl hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">
                    Total décaissé
                  </span>
                  <div 
                    className="w-8 h-8 flex items-center justify-center rounded-lg"
                    style={{ backgroundColor: `${COLORS.green}15`, color: COLORS.green }}
                  >
                    <TrendingUp className="w-4 h-4" />
                  </div>
                </div>
                <p className="text-2xl font-bold text-slate-900 tabular-nums">
                  {formatMontant(stats.totalMontant)}
                </p>
                
              </div>

              {/* Transactions */}
              <div className="p-4 bg-white border border-slate-200 rounded-xl hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">
                    Transactions
                  </span>
                  <div 
                    className="w-8 h-8 flex items-center justify-center rounded-lg"
                    style={{ backgroundColor: `${COLORS.primary}15`, color: COLORS.primary }}
                  >
                    <Accessibility className="w-4 h-4" />
                  </div>
                </div>
                <div className="flex items-baseline gap-2">
                  <p className="text-2xl font-bold text-slate-900 tabular-nums">{stats.total}</p>
                  <span className="text-xs text-slate-400">paiements</span>
                </div>
                <div className="flex items-center gap-3 mt-2">
                  <span className="text-xs font-medium" style={{ color: COLORS.green }}>{stats.clotures} clôturés</span>
                  <span className="text-xs font-medium" style={{ color: COLORS.accent }}>{stats.enCours} en cours</span>
                </div>
              </div>

              {/* Retenues & Avances */}
              <div className="p-4 bg-white border border-slate-200 rounded-xl hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">
                    Déductions
                  </span>
                  <div 
                    className="w-8 h-8 flex items-center justify-center rounded-lg"
                    style={{ backgroundColor: `${COLORS.accent}15`, color: COLORS.accent }}
                  >
                    <ArrowDownRight className="w-4 h-4" />
                  </div>
                </div>
                <p className="text-2xl font-bold text-slate-900 tabular-nums">
                  {formatMontant(stats.totalRetenues)}
                </p>
                <p className="text-xs text-slate-500 mt-1">déduits</p>
              </div>

              {/* Statut Global */}
              <div className="p-4 bg-white border border-slate-200 rounded-xl hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">
                    Statut global
                  </span>
                  <div 
                    className="w-8 h-8 flex items-center justify-center rounded-lg"
                    style={{ backgroundColor: `${COLORS.primary}15`, color: COLORS.primary }}
                  >
                    <CheckCircle2 className="w-4 h-4" />
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div>
                    <p className="text-xl font-bold text-slate-900 tabular-nums">{stats.clotures}</p>
                    <p className="text-[10px] text-slate-500 uppercase tracking-wide">Clôturés</p>
                  </div>
                  <div className="w-px h-10 bg-slate-200" />
                  <div>
                    <p className="text-xl font-bold text-slate-900 tabular-nums">{stats.enCours}</p>
                    <p className="text-[10px] text-slate-500 uppercase tracking-wide">En cours</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Search & Filters Bar */}
        <div className="flex-shrink-0 px-8 py-4 bg-white border-b border-slate-200">
          <div className="flex items-center gap-4">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Rechercher par nom, prénom..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-11 pr-4 py-2.5 text-sm border border-slate-200 bg-slate-50 rounded-xl placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:bg-white transition-all"
                style={{ '--tw-ring-color': `${COLORS.primary}40` }}
              />
            </div>

            {/* Filter Toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-5 py-2.5 text-sm font-medium border rounded-xl transition-all ${
                showFilters || hasActiveFilters
                  ? 'text-white border-transparent'
                  : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300'
              }`}
              style={showFilters || hasActiveFilters ? { backgroundColor: COLORS.primary } : {}}
            >
              <Filter className="w-4 h-4" />
              Filtres
              {activeFilterCount > 0 && (
                <span 
                  className={`text-xs px-2 py-0.5 font-semibold rounded-lg ${showFilters ? 'bg-white/20 text-white' : ''}`}
                  style={!showFilters ? { backgroundColor: COLORS.primary, color: 'white' } : {}}
                >
                  {activeFilterCount}
                </span>
              )}
            </button>

            {hasActiveFilters && (
              <button
                onClick={resetFilters}
                className="text-sm font-medium transition-colors hover:opacity-80"
                style={{ color: COLORS.accent }}
              >
                Réinitialiser
              </button>
            )}
          </div>

          {/* Expanded Filters */}
          {showFilters && (
            <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-slate-100">
              <div>
                <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-2">
                  Statut
                </label>
                <select
                  value={filterStatut}
                  onChange={(e) => setFilterStatut(e.target.value)}
                  className="w-full px-4 py-2.5 text-sm border border-slate-200 bg-white rounded-xl focus:outline-none focus:ring-2 transition-all appearance-none cursor-pointer"
                  style={{ '--tw-ring-color': `${COLORS.primary}40` }}
                >
                  <option value="tous">Tous les statuts</option>
                  <option value="paye">Payé</option>
                  <option value="cloture">Clôturé</option>
                </select>
              </div>

              <div>
                <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-2">
                  Période
                </label>
                <select
                  value={filterMois}
                  onChange={(e) => setFilterMois(e.target.value)}
                  className="w-full px-4 py-2.5 text-sm border border-slate-200 bg-white rounded-xl focus:outline-none focus:ring-2 transition-all appearance-none cursor-pointer"
                  style={{ '--tw-ring-color': `${COLORS.primary}40` }}
                >
                  <option value="tous">Tous les mois</option>
                  {moisUniques.map(mois => (
                    <option key={mois} value={mois}>{getMoisNom(mois)}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-2">
                  Exercice
                </label>
                <select
                  value={filterAnnee}
                  onChange={(e) => setFilterAnnee(e.target.value)}
                  className="w-full px-4 py-2.5 text-sm border border-slate-200 bg-white rounded-xl focus:outline-none focus:ring-2 transition-all appearance-none cursor-pointer"
                  style={{ '--tw-ring-color': `${COLORS.primary}40` }}
                >
                  <option value="tous">Toutes les années</option>
                  {anneesUniques.map(annee => (
                    <option key={annee} value={annee}>{annee}</option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>

        {/* Table Content */}
        <div className="flex-1 overflow-y-auto">

          {loading ? null : paiementsFiltres.length === 0 ? (


            <div className="flex flex-col items-center justify-center py-20">
              <div className="w-16 h-16 bg-slate-100 flex items-center justify-center rounded-2xl mb-4">
                <FileSpreadsheet className="w-7 h-7 text-slate-400" />
              </div>
              <p className="text-sm font-medium text-slate-900 mb-1">Aucun paiement trouvé</p>
              <p className="text-xs text-slate-500">Modifiez vos critères de recherche</p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200 sticky top-0 z-10">
                <tr>
                  <th className="px-6 py-3.5 text-left text-[10px] font-semibold uppercase tracking-widest text-slate-500">
                    Employé
                  </th>
                  <th className="px-4 py-3.5 text-left text-[10px] font-semibold uppercase tracking-widest text-slate-500">
                    Période
                  </th>
                  <th className="px-4 py-3.5 text-center text-[10px] font-semibold uppercase tracking-widest text-slate-500">
                    Présence
                  </th>
                  <th className="px-4 py-3.5 text-right text-[10px] font-semibold uppercase tracking-widest text-slate-500">
                    Brut
                  </th>
                  <th className="px-4 py-3.5 text-right text-[10px] font-semibold uppercase tracking-widest text-slate-500">
                    Déductions
                  </th>
                  <th className="px-4 py-3.5 text-right text-[10px] font-semibold uppercase tracking-widest text-slate-500">
                    Net
                  </th>
                  <th className="px-4 py-3.5 text-center text-[10px] font-semibold uppercase tracking-widest text-slate-500">
                    Statut
                  </th>
                  <th className="px-4 py-3.5 text-center text-[10px] font-semibold uppercase tracking-widest text-slate-500 w-24">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {paiementsFiltres.map((paiement) => {
                  const isExpanded = expandedRow === paiement.id;
                  const deductions = parseFloat(paiement.montant_retenues || 0) + parseFloat(paiement.montant_avances || 0);
                  
                  return (
                    <React.Fragment key={paiement.id}>
                      <tr className={`group transition-colors ${isExpanded ? 'bg-slate-50' : 'hover:bg-slate-50/50'}`}>
                        {/* Employé */}
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div 
                              className="w-10 h-10 flex items-center justify-center flex-shrink-0 rounded-xl text-white"
                              style={{ backgroundColor: COLORS.primary }}
                            >
                              <User className="w-5 h-5" />
                            </div>
                            <div>
                              <p className="text-sm font-semibold text-slate-900">
                                {paiement.employe_prenom} {paiement.employe_nom}
                              </p>
                              <p className="text-[11px] text-slate-500">
                                Personnel
                              </p>
                            </div>
                          </div>
                        </td>

                        {/* Période */}
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-2">
                            <CalendarDays className="w-3.5 h-3.5 text-slate-400" />
                            <span className="text-sm text-slate-700 font-medium tabular-nums">
                              {getMoisAbbrev(paiement.mois)} {paiement.annee}
                            </span>
                          </div>
                        </td>

                        {/* Présence */}
                        <td className="px-4 py-4 text-center">
                          <div className="inline-flex flex-col items-center">
                            <span className="text-sm font-semibold text-slate-900">
                              {paiement.jours_pointes || 0}/{paiement.jours_ouvres || 0}
                            </span>
                            <span className="text-[10px] text-slate-500">jours</span>
                          </div>
                        </td>

                        {/* Brut */}
                        <td className="px-4 py-4 text-right">
                          <span className="text-sm text-slate-600 tabular-nums">
                            { formatMontant(paiement.montant_brut || 0)}
                          </span>
                        </td>

                        {/* Déductions */}
                        <td className="px-4 py-4 text-right">
                          {deductions > 0 ? (
                            <span className="text-sm tabular-nums" style={{ color: COLORS.accent }}>
                              -{formatMontant(deductions, false)}
                            </span>
                          ) : (
                            <span className="text-sm text-slate-300">—</span>
                          )}
                        </td>

                        {/* Net */}
                        <td className="px-4 py-4 text-right">
                          <span className="text-sm font-bold text-slate-900 tabular-nums">
                            { formatMontant(paiement.montant_net || 0)}
                          </span>
                        </td>

                        {/* Statut */}
                        <td className="px-4 py-4">
                          <div className="flex justify-center">
                            {paiement.statut === 'cloture' ? (
                              <span 
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold rounded-lg"
                                style={{ backgroundColor: `${COLORS.green}15`, color: COLORS.green }}
                              >
                                <CheckCircle2 className="w-3.5 h-3.5" />
                                Clôturé
                              </span>
                            ) : (
                              <span 
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold rounded-lg"
                                style={{ backgroundColor: `${COLORS.secondary}15`, color: '#d97706' }}
                              >
                                <Clock className="w-3.5 h-3.5" />
                                En cours
                              </span>
                            )}
                          </div>
                        </td>

                        {/* Actions */}
                        <td className="px-4 py-4">
                          <div className="flex items-center justify-center gap-1">
                            <button
                              onClick={() => setExpandedRow(isExpanded ? null : paiement.id)}
                              className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-all rounded-lg"
                              title="Voir les détails"
                            >
                              {isExpanded ? (
                                <ChevronUp className="w-4 h-4" />
                              ) : (
                                <ChevronDown className="w-4 h-4" />
                              )}
                            </button>
                           <button
                              onClick={() => handleDownloadFiche(paiement.id)}
                              className="p-2 text-slate-400 hover:text-white transition-all rounded-lg"
                              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = COLORS.primary}
                              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                              title="Télécharger le bulletin"
                            >
                              <Download className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>

                      {/* Expanded Details */}
                      {isExpanded && (
                        <tr>
                          <td colSpan="8" className="px-6 py-6 bg-gradient-to-br from-slate-50 to-slate-100/50">
                            <div className="grid grid-cols-3 gap-6">
                              
                              {/* Section 1: Présence */}
                              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
                                <div 
                                  className="px-4 py-3 border-b border-slate-100 flex items-center gap-2"
                                  style={{ backgroundColor: `${COLORS.primary}10` }}
                                >
                                  <CalendarDays className="w-4 h-4" style={{ color: COLORS.primary }} />
                                  <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700">
                                    Présence
                                  </h4>
                                </div>
                                <table className="w-full text-sm">
                                  <tbody>

                                 <tr className="border-b border-slate-100">
                                      <td className="px-4 py-3 text-slate-600">Jours du mois</td>
                                      <td className="px-4 py-3 text-right font-semibold text-slate-900">
                                        {paiement.jours_ouvres || 0}
                                      </td>
                                    </tr>

                                    <tr className="border-b border-slate-100">
                                      <td className="px-4 py-3 text-slate-600">Jours pointés</td>
                                      <td className="px-4 py-3 text-right font-semibold text-slate-900">
                                        {paiement.jours_pointes || 0}
                                      </td>
                                    </tr>

                                    {paiement.jours_manquants > 0 && (
                                      <tr className="border-b border-slate-100 bg-red-50/30">
                                        <td className="px-4 py-3 text-slate-600">Jours manquants</td>
                                        <td className="px-4 py-3 text-right font-semibold" style={{ color: COLORS.accent }}>
                                          {paiement.jours_manquants}
                                        </td>
                                      </tr>
                                    )}
                                    
                                    <tr style={{ backgroundColor: `${COLORS.primary}08` }}>
                                      <td className="px-4 py-3 font-semibold text-slate-700">Taux présence</td>
                                      <td className="px-4 py-3 text-right font-bold" style={{ color: COLORS.primary }}>
                                        {paiement.jours_ouvres > 0 
                                          ? Math.round((paiement.jours_pointes / paiement.jours_ouvres) * 100) 
                                          : 0}%
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>

                              {/* Section 2: Détail Rémunération */}
                              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
                                <div 
                                  className="px-4 py-3 border-b border-slate-100 flex items-center gap-2"
                                  style={{ backgroundColor: `${COLORS.green}10` }}
                                >
                                  <Banknote className="w-4 h-4" style={{ color: COLORS.green }} />
                                  <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700">
                                    Détail rémunération
                                  </h4>
                                </div>

                                <table className="w-full text-sm">
                                  <tbody>
                                    {/* Brut */}
                                    <tr className="border-b border-slate-100">
                                      <td className="px-4 py-3 text-slate-600">
                                        {paiement.type_salaire === 'hourly' && paiement.total_heures
                                          ? `${paiement.total_heures}h × ${formatMontant(paiement.taux_horaire)}/h`
                                          : 'Salaire brut'}
                                      </td>
                                      <td className="px-4 py-3 text-right font-semibold text-slate-900 tabular-nums">
                                        {formatMontant(paiement.montant_brut || 0)}
                                      </td>
                                    </tr>

                                    {/* Déductions ponctualité — horaire uniquement */}
                                    {paiement.type_salaire === 'hourly' && parseFloat(paiement.retenue_ponctualite || 0) > 0 && (
                                      <tr className="border-b border-slate-100 bg-red-50/30">
                                        <td className="px-4 py-3 text-slate-600">Déductions ponctualité</td>
                                        <td className="px-4 py-3 text-right font-semibold tabular-nums" style={{ color: COLORS.accent }}>
                                          -{formatMontant(paiement.retenue_ponctualite, false)}
                                        </td>
                                      </tr>
                                    )}

                                    {/* CNSS */}
                                    {parseFloat(paiement.retenue_cnss || 0) > 0 && (
                                      <tr className="border-b border-slate-100 bg-blue-50/30">
                                        <td className="px-4 py-3 text-slate-600">Cotisation sociale</td>
                                        <td className="px-4 py-3 text-right font-semibold text-blue-600 tabular-nums">
                                          -{formatMontant(paiement.retenue_cnss, false)}
                                        </td>
                                      </tr>
                                    )}

                                    {/* Retenues manuelles — mensuel uniquement */}
                                    {paiement.type_salaire !== 'hourly' && parseFloat(paiement.montant_retenues || 0) > 0 && (
                                      <tr className="border-b border-slate-100 bg-red-50/30">
                                        <td className="px-4 py-3 text-slate-600">
                                          Retenues
                                          {paiement.justification_retenue && (
                                            <span className="block text-xs text-slate-400 mt-0.5">{paiement.justification_retenue}</span>
                                          )}
                                        </td>
                                        <td className="px-4 py-3 text-right font-semibold tabular-nums" style={{ color: COLORS.accent }}>
                                          -{formatMontant(paiement.montant_retenues, false)}
                                        </td>
                                      </tr>
                                    )}

                                    {/* Retenues manuelles supplémentaires — horaire */}
                                    {paiement.type_salaire === 'hourly' && 
                                      parseFloat(paiement.montant_retenues || 0) > 
                                      (parseFloat(paiement.retenue_ponctualite || 0) + parseFloat(paiement.retenue_cnss || 0)) && (
                                      <tr className="border-b border-slate-100 bg-red-50/30">
                                        <td className="px-4 py-3 text-slate-600">Retenues manuelles</td>
                                        <td className="px-4 py-3 text-right font-semibold tabular-nums" style={{ color: COLORS.accent }}>
                                          -{formatMontant(
                                            parseFloat(paiement.montant_retenues || 0) - 
                                            parseFloat(paiement.retenue_ponctualite || 0) - 
                                            parseFloat(paiement.retenue_cnss || 0),
                                            false
                                          )}
                                        </td>
                                      </tr>
                                    )}

                                    {/* Avances */}
                                    {parseFloat(paiement.montant_avances || 0) > 0 && (
                                      <tr className="border-b border-slate-100 bg-orange-50/30">
                                        <td className="px-4 py-3 text-slate-600">Avances récupérées</td>
                                        <td className="px-4 py-3 text-right font-semibold text-orange-600 tabular-nums">
                                          -{formatMontant(paiement.montant_avances, false)}
                                        </td>
                                      </tr>
                                    )}

                                    {/* Primes */}
                                    {parseFloat(paiement.montant_primes || 0) > 0 && (
                                      <tr className="border-b border-slate-100 bg-green-50/30">
                                        <td className="px-4 py-3 text-slate-600">Primes</td>
                                        <td className="px-4 py-3 text-right font-semibold text-green-600 tabular-nums">
                                          +{formatMontant(paiement.montant_primes, false)}
                                        </td>
                                      </tr>
                                    )}

                                    {/* Net */}
                                    <tr style={{ backgroundColor: `${COLORS.green}10` }}>
                                      <td className="px-4 py-4 font-bold text-slate-900">Net à payer</td>
                                      <td className="px-4 py-4 text-right text-lg font-bold tabular-nums" style={{ color: COLORS.green }}>
                                        {formatMontant(paiement.montant_net || 0)}
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>



                                <div className="px-4 py-3 border-t border-slate-100 bg-slate-50/50">
                                  <div className="flex justify-between items-center">
                                    <span className="text-xs text-slate-500">Mode de règlement</span>
                                    <span 
                                      className="text-xs font-semibold px-2.5 py-1 rounded-lg"
                                      style={{ backgroundColor: `${COLORS.primary}15`, color: COLORS.primary }}
                                    >
                                      {paiement.mode_paiement_nom || 'Non spécifié'}
                                    </span>
                                  </div>
                                </div>
                              </div>

                              {/* Section 3: Traçabilité */}
                              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
                                <div 
                                  className="px-4 py-3 border-b border-slate-100 flex items-center gap-2"
                                  style={{ backgroundColor: `${COLORS.secondary}10` }}
                                >
                                  <CheckCircle2 className="w-4 h-4" style={{ color: '#d97706' }} />
                                  <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700">
                                    Traçabilité
                                  </h4>
                                </div>
                                <table className="w-full text-sm">
                                  <tbody>

                                   <tr className="border-b border-slate-100">
                                    <td className="px-4 py-3 text-slate-600">Validé par</td>
                                    <td className="px-4 py-3 text-right font-semibold text-slate-900">
                                      {paiement.validateur_nom || 'Système'}
                                    </td>
                                  </tr>


                                    <tr className="border-b border-slate-100">
                                      <td className="px-4 py-3 text-slate-600">Date paiement</td>
                                      <td className="px-4 py-3 text-right font-semibold text-slate-900">
                                        {new Date(paiement.date_paiement).toLocaleDateString('fr-FR', {
                                          day: 'numeric',
                                          month: 'short',
                                          year: 'numeric'
                                        })}
                                      </td>
                                    </tr>
                                    {paiement.date_cloture && (
                                      <>
                                        <tr className="border-b border-slate-100" style={{ backgroundColor: `${COLORS.green}08` }}>
                                          <td className="px-4 py-3 text-slate-600">Date clôture</td>
                                          <td className="px-4 py-3 text-right font-semibold" style={{ color: COLORS.green }}>
                                            {new Date(paiement.date_cloture).toLocaleDateString('fr-FR', {
                                              day: 'numeric',
                                              month: 'short',
                                              year: 'numeric'
                                            })}
                                          </td>
                                        </tr>

                                        {paiement.cloture_nom && (
                                        <tr className="border-b border-slate-100">
                                          <td className="px-4 py-3 text-slate-600">Clôturé par</td>
                                          <td className="px-4 py-3 text-right font-semibold text-slate-900">
                                            {paiement.cloture_nom}
                                          </td>
                                        </tr>
                                      )}
                                     </>
                                    )}
                                    
                                  </tbody>
                                </table>

                                {/* Justification si présente */}
                                {paiement.justification_pointages_manquants && (
                                  <div className="px-4 py-3 border-t border-slate-100" style={{ backgroundColor: `${COLORS.accent}08` }}>
                                    <div className="flex items-start gap-2">
                                      <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: COLORS.accent }} />
                                      <div>
                                        <p className="text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: COLORS.accent }}>
                                          Justification absences
                                        </p>
                                        <p className="text-xs text-slate-700 leading-relaxed">
                                          {paiement.justification_pointages_manquants}
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Bouton téléchargement */}
                          <div className="flex justify-end mt-5 pt-5 border-t border-slate-200">
                              <button
                                onClick={() => handleDownloadFiche(paiement.id)}
                                className="flex items-center gap-2 px-6 py-3 text-white text-sm font-semibold rounded-xl hover:opacity-90 transition-all hover:shadow-lg"
                                style={{ backgroundColor: COLORS.primary }}
                              >
                                <Download className="w-4 h-4" />
                                Télécharger le bulletin de paie
                              </button>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
  );
};

export default HoraPilotPaiementHistory;